import { Lock } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/system';
import { useEffect } from 'react';

import { incorrectSound as incorrectSoundAsset } from '@/assets';
import { AssessmentAnswer, QuizAnswer } from '@/types';
import { isTestModeEnabled } from '@/utils';

import { CheckmarkAnimation } from '../CheckmarkAnimation';
import { Skeleton } from '../Skeleton';

interface AnswerBoxProps {
  answer: QuizAnswer | AssessmentAnswer;
  onSelect: (value: string | number) => void;
  isSelected?: boolean;
  index: number;
  isPending?: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
  questionId: string;
  showCaption?: boolean;
  isCorrect?: boolean;
  isIncorrect?: boolean;
}

const incorrectSound = new Audio(incorrectSoundAsset);
incorrectSound.volume = 0.25;

export const AnswerBox = ({
  onSelect,
  answer,
  isSelected = false,
  index,
  isPending,
  isDisabled,
  isLocked,
  questionId,
  showCaption,
  isCorrect,
  isIncorrect,
}: AnswerBoxProps) => {
  const isTestMode = isTestModeEnabled();
  const value =
    (answer as QuizAnswer).id ||
    (answer as AssessmentAnswer).assessmentAnswerId;

  const answerLetter = String.fromCharCode(65 + index);

  useEffect(() => {
    if (isIncorrect) {
      incorrectSound.play();
    }
  }, [isIncorrect]);

  return (
    <>
      <Box
        component="label"
        sx={[
          styles.answerContainer,
          isSelected && styles.answerSelected,
          !isSelected && !isDisabled && !isLocked && styles.answerClickable,
          !!isIncorrect && styles.answerIncorrect,
          (!!isCorrect || (isTestMode && !!answer.isCorrect)) &&
            styles.answerCorrect,
        ]}>
        {isPending ? (
          <Skeleton animation={false} />
        ) : (
          <>
            {isCorrect && (
              <CheckmarkAnimation
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                }}
              />
            )}
            {isLocked && <Lock sx={styles.lockIcon} />}
            <Typography
              sx={[
                styles.answerLetter,
                isSelected && styles.letterSelected,
                !!isCorrect && styles.letterCorrect,
                !!isIncorrect && styles.letterIncorrect,
              ]}
              variant="body3SemiBold">
              {answerLetter}
            </Typography>
            <Typography variant={isSelected ? 'body2SemiBold' : 'body2'}>
              {'answerLabel' in answer ? answer.answerLabel : answer.answer}
              <input
                style={{ opacity: 0, pointerEvents: 'none', position: 'fixed' }}
                type="radio"
                name={questionId}
                value={value}
                disabled={isDisabled}
                checked={isSelected}
                onChange={ev => {
                  ev.stopPropagation();
                  onSelect(value);
                }}
                tabIndex={isDisabled || isLocked ? -1 : 0}
              />
            </Typography>
          </>
        )}
      </Box>
      {showCaption && (
        <>
          <Typography
            variant="body2SemiBold"
            sx={{ mt: 1 }}
            color={answer.isCorrect ? 'green' : 'red'}>
            {answerLetter}) {answer.isCorrect ? 'Correct' : 'Incorrect'}:&nbsp;
          </Typography>
          <Typography variant="body3" color="grey175">
            {(answer as QuizAnswer).caption}
          </Typography>
        </>
      )}
    </>
  );
};

const styles = {
  answerContainer: {
    userSelect: 'none',
    display: 'block',
    position: 'relative',
    border: '3px solid',
    borderColor: 'grey100',
    textAlign: 'center',
    borderRadius: 4,
    px: 2,
    py: 4,
    minHeight: 70,
    height: '100%',
    backgroundColor: 'surface.background',
    color: 'grey175',
    alignContent: 'center',
    '&:focus-within': ({ palette }: Theme) => ({
      outline: `${palette.secondary.active} solid 2px`,
    }),
  },
  answerLetter: {
    position: 'absolute',
    top: 0,
    left: 0,
    p: 1,
  },
  answerClickable: {
    cursor: 'pointer',
  },
  answerSelected: {
    borderColor: 'secondary.main',
    backgroundColor: 'secondary.medium',
  },
  answerCorrect: {
    borderColor: 'green',
  },
  answerIncorrect: {
    borderColor: 'red',
  },
  letterSelected: {
    color: 'secondary.main',
  },
  letterCorrect: {
    color: 'green',
  },
  letterIncorrect: {
    color: 'red',
  },
  lockIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    height: 16,
    width: 16,
    color: 'grey100',
  },
};
